export type HelperFetchRequestInit = RequestInit & {
  timeout?: number | undefined;
  throwErrorOnNotFound?: boolean;
};

const ORDINAL_RULES = new Intl.PluralRules("en-US", { type: "ordinal" });
const ORDINAL_SUFFIXES = new Map([
  ["one", "st"],
  ["two", "nd"],
  ["few", "rd"],
  ["other", "th"],
]);

function ordinal(n: number) {
  const rule = ORDINAL_RULES.select(n);
  const suffix = ORDINAL_SUFFIXES.get(rule);
  return `${n}${suffix}`;
}

export const helperFetch = async (
  url: string,
  options: HelperFetchRequestInit = {},
  retryOnRateLimitExceeded: boolean = true,
) => {
  const defaultTimeout: number = 20_000;

  const timeout = options.timeout ?? defaultTimeout;

  const performFetch = async (): Promise<Response> => {
    const fetchStart = new Date().getTime();
    const cache = options.cache ?? "force-cache";
    try {
      return await fetch(url, {
        ...options,
        cache,
        signal: AbortSignal.timeout(timeout),
        // 'no-store' must not be combined with a revalidate hint or Next.js ignores it and caches anyway.
        ...(cache === "no-store" ? {} : { next: { revalidate: 60 } }),
      });
    } catch (e) {
      const fetchTime = new Date().getTime() - fetchStart;
      // Stack trace show no info about what request timed out so we need to log that our selves
      if (e.name === "TimeoutError") {
        console.error(`${options.method} request to ${url} timed out after ${fetchTime} ms`);
      }
      if (e.name === "AbortError") {
        console.error(`${options.method} request to ${url} aborted after ${fetchTime} ms`);
      }
      throw e;
    }
  };

  let response = await performFetch();

  // Retry on rate limit exceeded
  const maxRetries = 10;
  for (
    let attempt = 1;
    response.status === 429 && retryOnRateLimitExceeded && attempt <= maxRetries;
    attempt++
  ) {
    // eslint-disable-next-line sonarjs/pseudo-random
    const jitter = Math.random() * 500;
    const retryAfter = (attempt - 1) * 300 + jitter;
    console.debug(
      `Retrying request to Contentful a ${ordinal(attempt)} time in ${Math.round(retryAfter)} ms`,
    );
    await new Promise<void>((resolve) => setTimeout(resolve, retryAfter));
    response = await performFetch();
  }

  if (!response.ok) {
    const throwErrorOnNotFound = options.throwErrorOnNotFound ?? true;
    if (response.status !== 404 || throwErrorOnNotFound) {
      throw new Error(
        `${options.method ?? "GET"} request to ${url} failed with status code ${
          response.status
        } and response body "${await response.text()}"`,
      );
    }
  }

  return response;
};
